/**
 * Menu-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";

// Types
export interface MenuItemCOGS {
  foodCost: number;
  packaging: {
    toast: string[];
    uber: string[];
    doordash: string[];
  };
  platformData: {
    toast: { packaging: string[]; price: number; available?: boolean; promotionalCampaign?: { type: 'BOGO' | 'percentage_off' | 'fixed_amount'; active: boolean; details: any; }; };
    uber: { packaging: string[]; price: number; available?: boolean; promotionalCampaign?: { type: 'BOGO' | 'percentage_off' | 'fixed_amount'; active: boolean; details: any; }; };
    doordash: { packaging: string[]; price: number; available?: boolean; promotionalCampaign?: { type: 'BOGO' | 'percentage_off' | 'fixed_amount'; active: boolean; details: any; }; };
  };
}

export interface MenuItem {
  id: string;
  name: string;
  price: number;
  deliveryPrice?: number;
  estimatedCost: number;
  ingredientsList: string;
  ingredients?: { ingredientId: string; quantity: number; type: 'raw' | 'prep'; }[];
  comboItems?: string[];
  sales: number;
  category?: string;
  categoryId?: string;
  cogsData?: MenuItemCOGS;
}

export interface Ingredient {
  id: string;
  name: string;
  purchase_quantity: number;
  purchase_unit: string;
  total_purchase_price: number;
  purchase_date: string;
  supplier?: string;
  receipt_description?: string;
  category?: string;
  standard_unit?: string;
  cost_per_standard_unit?: number;
  conversion_factor?: number;
  updated_at?: string;
  // Mapped fields
  costPerStandardUnit: number;
  standardUnit?: string;
  purchaseUnit?: string;
}

export interface PrepItemIngredient {
  ingredientId: string;
  quantity: number;
  cost: number;
  ingredientName: string;
}

export interface PrepItem {
  id: string;
  name: string;
  yieldAmount: number;
  yieldUnit: string;
  ingredients: PrepItemIngredient[];
  totalCost: number;
  costPerUnit: number;
}

export interface MenuCategory {
  id: string;
  name: string;
}

/**
 * Get all menu items for a company
 */
export async function getMenuItems(companyId: string): Promise<MenuItem[]> {
  if (!companyId) return [];

  const { data: itemsData, error } = await supabase
    .from("menu_items")
    .select(`
      id,
      name,
      price,
      delivery_price,
      estimated_cost,
      ingredients_list,
      sales,
      category_id,
      cogs_data,
      menu_categories (id, name),
      menu_item_ingredients (
        ingredient_id,
        prep_item_id,
        quantity,
        ingredient_type
      ),
      menu_item_combo_items!menu_item_combo_items_menu_item_id_fkey (
        combo_item_id
      )
    `)
    .eq("company_id", companyId)
    .order("name");

  if (error) throw error;

  const formattedMenuItems = (itemsData || []).map((item: any) => {
    const raw = item.cogs_data as Record<string, any> | undefined;
    if (raw?.platformData?.doorDash && !raw.platformData.doordash) {
      raw.platformData.doordash = raw.platformData.doorDash;
      delete raw.platformData.doorDash;
    }
    if (raw?.packaging?.doorDash && !raw.packaging.doordash) {
      raw.packaging.doordash = raw.packaging.doorDash;
      delete raw.packaging.doorDash;
    }
    return {
      id: item.id,
      name: item.name,
      price: Number(item.price),
      deliveryPrice: item.delivery_price ? Number(item.delivery_price) : undefined,
      estimatedCost: Number(item.estimated_cost),
      ingredientsList: item.ingredients_list || '',
      ingredients: (item.menu_item_ingredients || []).map((ing: any) => ({
        ingredientId: ing.ingredient_type === 'raw' ? ing.ingredient_id : ing.prep_item_id,
        quantity: Number(ing.quantity),
        type: ing.ingredient_type,
      })),
      comboItems: (item.menu_item_combo_items || []).map((combo: any) => combo.combo_item_id),
      sales: item.sales,
      category: item.menu_categories?.name,
      categoryId: item.category_id,
      cogsData: raw as unknown as MenuItemCOGS | undefined,
    };
  });

  return formattedMenuItems as MenuItem[];
}

/**
 * Get all ingredients for a company
 */
export async function getIngredients(companyId: string): Promise<Ingredient[]> {
  if (!companyId) return [];

  const [{ data, error }, { data: vendorMappings }] = await Promise.all([
    supabase
      .from("ingredients")
      .select("id, name, purchase_quantity, purchase_unit, total_purchase_price, purchase_date, supplier, receipt_description, category, standard_unit, cost_per_standard_unit, conversion_factor, updated_at")
      .eq("company_id", companyId)
      .order("name")
      .order("updated_at", { ascending: false }),
    supabase
      .from("vendor_ingredient_mappings")
      .select("ingredient_id, cost_per_recipe_unit, recipe_unit")
      .eq("company_id", companyId)
      .not("ingredient_id", "is", null),
  ]);

  if (error) throw error;

  const vendorCostMap = new Map<string, { cost: number; unit: string }>();
  (vendorMappings || []).forEach((m: any) => {
    if (m.ingredient_id && m.cost_per_recipe_unit > 0) {
      const existing = vendorCostMap.get(m.ingredient_id);
      if (!existing || m.cost_per_recipe_unit > existing.cost) {
        vendorCostMap.set(m.ingredient_id, {
          cost: m.cost_per_recipe_unit,
          unit: m.recipe_unit || "",
        });
      }
    }
  });

  // Deduplicate by name (case-insensitive) - keep the first encountered (most recently updated due to ordering)
  const seen = new Set<string>();
  const deduped: any[] = [];
  for (const ing of (data || [])) {
    const key = (ing.name || '').toLowerCase().trim();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(ing);
    }
  }

  return deduped.map((ing: any) => {
    let costPerStandardUnit = Number(ing.cost_per_standard_unit) || 0;
    let standardUnit = ing.standard_unit;

    if (costPerStandardUnit === 0) {
      const vendorCost = vendorCostMap.get(ing.id);
      if (vendorCost) {
        costPerStandardUnit = vendorCost.cost;
        standardUnit = vendorCost.unit || standardUnit;
      }
    }

    return {
      ...ing,
      standard_unit: standardUnit || "",
      cost_per_standard_unit: costPerStandardUnit,
      costPerStandardUnit,
      standardUnit: standardUnit || "",
      purchaseUnit: ing.purchase_unit,
    };
  }) as Ingredient[];
}

/**
 * Get all prep items for a company
 */
export async function getPrepItems(companyId: string): Promise<PrepItem[]> {
  if (!companyId) return [];

  const { data: prepData, error } = await supabase
    .from("prep_items")
    .select(`
      id,
      name,
      yield_amount,
      yield_unit,
      total_cost,
      cost_per_unit,
      prep_item_ingredients (
        ingredient_id,
        quantity,
        cost,
        ingredient_name
      )
    `)
    .eq("company_id", companyId)
    .order("name");

  if (error) throw error;

  const formattedPrepItems = (prepData || []).map((item: any) => ({
    id: item.id,
    name: item.name,
    yieldAmount: Number(item.yield_amount),
    yieldUnit: item.yield_unit,
    ingredients: (item.prep_item_ingredients || []).map((ing: any) => ({
      ingredientId: ing.ingredient_id,
      quantity: Number(ing.quantity),
      cost: Number(ing.cost),
      ingredientName: ing.ingredient_name,
    })),
    totalCost: Number(item.total_cost),
    costPerUnit: Number(item.cost_per_unit),
  }));

  return formattedPrepItems as PrepItem[];
}

/**
 * Get all menu categories for a company
 */
export async function getMenuCategories(companyId: string): Promise<MenuCategory[]> {
  if (!companyId) return [];

  const { data, error } = await supabase
    .from("menu_categories")
    .select("id, name")
    .eq("company_id", companyId)
    .order("name");

  if (error) throw error;
  return (data || []) as MenuCategory[];
}

/**
 * Delete a menu category and unassign all menu items from it.
 */
export async function deleteMenuCategory(
  companyId: string,
  categoryId: string
): Promise<void> {
  if (!companyId || !categoryId) return;

  const { error: updateError } = await supabase
    .from("menu_items")
    .update({ category_id: null })
    .eq("company_id", companyId)
    .eq("category_id", categoryId);

  if (updateError) throw updateError;

  const { error: deleteError } = await supabase
    .from("menu_categories")
    .delete()
    .eq("id", categoryId)
    .eq("company_id", companyId);

  if (deleteError) throw deleteError;
}
